import axios from 'axios';
import { IQueryRepository, FeatureEntity, ResponseValueObject, StatisticDefinition, LayerStatisticEntity, ExtentEntity, LayerEntity, LayerTypeEnum } from '@gishub/domain';

export class ArcGisQueryRepository implements IQueryRepository {
  private readonly _proxyUrl: string | null;

  constructor(proxyUrl?: string | null) {
    this._proxyUrl = proxyUrl ? proxyUrl.replace(/\/+$/, '') : null;
  }

  resolveUrl(url: string, useProxy?: boolean): string {
    if (useProxy && this._proxyUrl) {
      const base = this._proxyUrl.includes('?')
        ? this._proxyUrl.split('?')[0] + '?'
        : this._proxyUrl + '?';
      return `${base}${url}`;
    }
    return url;
  }

  async queryLayer(
    url: string,
    where = '1=1',
    outFields = '*',
    returnGeometry = false,
    outSR?: number,
    useProxy?: boolean,
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      const params: Record<string, any> = { where, outFields, returnGeometry, f: 'json' };
      if (outSR != null) params.outSR = outSR;

      let finalUrl = `${url}/query`;
      const getOptions: any = { timeout: 120_000 };

      if (useProxy && this._proxyUrl) {
        const proxyBase = this._proxyUrl.includes('?')
          ? this._proxyUrl.split('?')[0] + '?'
          : this._proxyUrl + '?';
        const targetQuery = new URLSearchParams(params).toString();
        finalUrl = `${proxyBase}${url}/query?${targetQuery}`;
      } else {
        getOptions.params = params;
      }

      const response = await axios.get(finalUrl, getOptions);
      const raw = response.data as { features?: { attributes: Record<string, any>; geometry?: any }[]; error?: { code: number; message: string; details?: string[] } };
      if (raw.error) {
        return new ResponseValueObject(false, [], raw.error.message);
      }
      const features: FeatureEntity[] = (raw.features ?? []).map((f) => ({
        attributes: f.attributes,
        geometry: f.geometry,
      }));
      return new ResponseValueObject(true, features);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer');
    }
  }

  async queryByPoint(
    entity: LayerEntity,
    point: { x: number; y: number; spatialReference: { wkid: number } },
    resolution?: number
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      switch (entity.type) {
        case LayerTypeEnum.FeatureServer:
        case LayerTypeEnum.SubServer:
          return await this._queryFeatureLayerByPoint(entity.url!, point, entity.hasProxy, resolution);
        case LayerTypeEnum.WMS:
          return await this._queryWMSLayerByPoint(entity, point);
        default:
          return new ResponseValueObject(true, []);
      }
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying by point');
    }
  }

  private async _queryFeatureLayerByPoint(
    url: string,
    point: { x: number; y: number; spatialReference: { wkid: number } },
    useProxy?: boolean,
    resolution?: number,
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    const distance = resolution ? Math.round(resolution * 20) : 50;
    const params: Record<string, any> = {
      geometry: JSON.stringify({ x: point.x, y: point.y }),
      geometryType: 'esriGeometryPoint',
      spatialRel: 'esriSpatialRelIntersects',
      distance,
      units: 'esriSRUnit_Meter',
      inSR: point.spatialReference.wkid,
      outFields: '*',
      returnGeometry: false,
      f: 'json',
    };

    let finalUrl = `${url}/query`;
    if (useProxy && this._proxyUrl) {
      const proxyBase = this._proxyUrl.includes('?')
        ? this._proxyUrl.split('?')[0] + '?'
        : this._proxyUrl + '?';
      finalUrl = `${proxyBase}${url}/query`;
    }

    const response = await axios.post(finalUrl, new URLSearchParams(params), { timeout: 30_000 });
    const raw = response.data as { features?: { attributes: Record<string, any> }[] };
    const features: FeatureEntity[] = (raw.features ?? []).map((f) => ({ attributes: f.attributes }));
    return new ResponseValueObject(true, features);
  }

  private async _queryWMSLayerByPoint(
    _entity: LayerEntity,
    _point: { x: number; y: number; spatialReference: { wkid: number } },
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    return new ResponseValueObject(true, []);
  }

  async queryLayerStatistics(
    url: string,
    where = '1=1',
    outStatistics?: StatisticDefinition[],
    groupByFields?: string[],
    geometry?: any,
    geometryType?: string,
    spatialRel?: string,
    useProxy?: boolean,
  ): Promise<ResponseValueObject<LayerStatisticEntity[]>> {
    try {
      const params: Record<string, any> = { where, f: 'json' };

      if (outStatistics && outStatistics.length > 0) {
        params.outStatistics = JSON.stringify(outStatistics);
      }
      if (groupByFields && groupByFields.length > 0) {
        params.groupByFieldsForStatistics = groupByFields.join(',');
      }
      if (geometry) {
        params.geometry = typeof geometry === 'string' ? geometry : JSON.stringify(geometry);
        params.geometryType = geometryType ?? 'esriGeometryEnvelope';
        params.spatialRel = spatialRel ?? 'esriSpatialRelIntersects';
        params.inSR = geometry?.spatialReference?.wkid ?? '4326';
      }

      const usePost = !!geometry;
      let response;

      if (usePost) {
        let finalUrl = `${url}/query`;
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          finalUrl = `${proxyBase}${url}/query`;
        }
        response = await axios.post(finalUrl, new URLSearchParams(params), { timeout: 120_000 });
      } else {
        let finalUrl = `${url}/query`;
        const getOptions: any = { timeout: 120_000 };
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          const targetQuery = new URLSearchParams(params).toString();
          finalUrl = `${proxyBase}${url}/query?${targetQuery}`;
        } else {
          getOptions.params = params;
        }
        response = await axios.get(finalUrl, getOptions);
      }

      const data = response.data as { features?: { attributes: Record<string, any> }[]; error?: { message: string } };
      if (data.error) {
        return new ResponseValueObject(false, [], data.error.message);
      }
      const stats: LayerStatisticEntity[] = (data.features ?? []).map((f) => f.attributes);
      return new ResponseValueObject(true, stats);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer statistics');
    }
  }

  async queryLayerCount(
    url: string,
    where = '1=1',
    geometry?: any,
    geometryType?: string,
    spatialRel?: string,
    useProxy?: boolean,
  ): Promise<ResponseValueObject<number>> {
    try {
      const params: Record<string, any> = { where, returnCountOnly: true, f: 'json' };

      if (geometry) {
        params.geometry = typeof geometry === 'string' ? geometry : JSON.stringify(geometry);
        params.geometryType = geometryType ?? 'esriGeometryEnvelope';
        params.spatialRel = spatialRel ?? 'esriSpatialRelIntersects';
        params.inSR = geometry?.spatialReference?.wkid ?? '4326';
      }

      const usePost = !!geometry;
      let response;

      if (usePost) {
        let finalUrl = `${url}/query`;
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          finalUrl = `${proxyBase}${url}/query`;
        }
        response = await axios.post(finalUrl, new URLSearchParams(params), { timeout: 120_000 });
      } else {
        let finalUrl = `${url}/query`;
        const getOptions: any = { timeout: 120_000 };
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          const targetQuery = new URLSearchParams(params).toString();
          finalUrl = `${proxyBase}${url}/query?${targetQuery}`;
        } else {
          getOptions.params = params;
        }
        response = await axios.get(finalUrl, getOptions);
      }

      const data = response.data as { count?: number; error?: { message: string } };
      if (data.error) {
        return new ResponseValueObject(false, 0, data.error.message);
      }
      const count = data.count ?? 0;
      return new ResponseValueObject(true, count);
    } catch (err: any) {
      return new ResponseValueObject(false, 0, err?.message ?? 'Error counting layer features');
    }
  }

  async queryLayerFeatures(
    url: string,
    where = '1=1',
    geometry?: any,
    geometryType?: string,
    spatialRel?: string,
    outFields = '*',
    returnGeometry = true,
    resultOffset?: number,
    resultRecordCount?: number,
    useProxy?: boolean,
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      const params: Record<string, any> = { where, outFields, returnGeometry, f: 'json' };
      if (resultOffset != null) params.resultOffset = resultOffset;
      if (resultRecordCount != null) params.resultRecordCount = resultRecordCount;
      if (geometry) {
        params.geometry = typeof geometry === 'string' ? geometry : JSON.stringify(geometry);
        params.geometryType = geometryType ?? 'esriGeometryEnvelope';
        params.spatialRel = spatialRel ?? 'esriSpatialRelIntersects';
        params.inSR = geometry?.spatialReference?.wkid ?? '4326';
      }

      const usePost = !!geometry;
      let response;

      if (usePost) {
        let finalUrl = `${url}/query`;
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          finalUrl = `${proxyBase}${url}/query`;
        }
        response = await axios.post(finalUrl, new URLSearchParams(params), { timeout: 120_000 });
      } else {
        let finalUrl = `${url}/query`;
        const getOptions: any = { timeout: 120_000 };
        if (useProxy && this._proxyUrl) {
          const proxyBase = this._proxyUrl.includes('?')
            ? this._proxyUrl.split('?')[0] + '?'
            : this._proxyUrl + '?';
          const targetQuery = new URLSearchParams(params).toString();
          finalUrl = `${proxyBase}${url}/query?${targetQuery}`;
        } else {
          getOptions.params = params;
        }
        response = await axios.get(finalUrl, getOptions);
      }

      const data = response.data as { features?: { attributes: Record<string, any>; geometry?: any }[]; error?: { message: string } };
      if (data.error) {
        return new ResponseValueObject(false, [], data.error.message);
      }
      const features: FeatureEntity[] = (data.features ?? []).map((f) => ({
        attributes: f.attributes,
        geometry: f.geometry,
      }));
      return new ResponseValueObject(true, features);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer features');
    }
  }

  async queryLayerExtent(
    url: string,
    where = '1=1',
    useProxy?: boolean,
  ): Promise<ResponseValueObject<ExtentEntity | null>> {
    try {
      const params: Record<string, any> = { where, returnExtentOnly: true, f: 'json' };
      let finalUrl = `${url}/query`;
      const getOptions: any = { timeout: 120_000 };

      if (useProxy && this._proxyUrl) {
        const proxyBase = this._proxyUrl.includes('?')
          ? this._proxyUrl.split('?')[0] + '?'
          : this._proxyUrl + '?';
        const targetQuery = new URLSearchParams(params).toString();
        finalUrl = `${proxyBase}${url}/query?${targetQuery}`;
      } else {
        getOptions.params = params;
      }

      const response = await axios.get(finalUrl, getOptions);
      const data = response.data as { extent?: ExtentEntity; error?: { message: string } };
      if (data.error) {
        return new ResponseValueObject(false, null, data.error.message);
      }
      return new ResponseValueObject(true, data.extent ?? null);
    } catch (err: any) {
      return new ResponseValueObject(false, null, err?.message ?? 'Error getting layer extent');
    }
  }
}
